import { forwardRef, useEffect, useId, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { useCatalogSearch, type CatalogItem } from '@/routes/estimates/useEstimates';

/**
 * The builder's price-book search (M2-06): type, Enter, type, Enter.
 *
 * The same WAI-ARIA combobox-over-listbox shape as `PartyPicker`, with one
 * difference that is the point of it: the FIRST match is active as soon as
 * results arrive, so Enter adds it without reaching for an arrow key. The
 * AC's "building a 10-line estimate should take under a minute" is ten
 * searches and ten Enters, with focus staying in this box between them.
 */
export const CatalogSearch = forwardRef<HTMLInputElement, { onAdd: (item: CatalogItem) => void }>(
  function CatalogSearch({ onAdd }, ref) {
    const { t } = useTranslation();
    const id = useId();
    const listboxId = `${id}-catalog-listbox`;
    const optionId = (index: number) => `${id}-catalog-option-${index}`;

    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    // Enter pressed before the search caught up with the typing: honoured as
    // soon as it does, so "type, Enter" never needs a pause in between.
    const [enterPending, setEnterPending] = useState(false);

    const search = useCatalogSearch(term);
    const results = term.trim() === '' ? [] : (search.data ?? []);
    const expanded = open && results.length > 0;
    const noMatches = open && term.trim() !== '' && search.fresh && results.length === 0;

    // A new set of results starts at the top, so Enter takes the best match.
    useEffect(() => {
      setActiveIndex(0);
    }, [search.data]);

    function add(item: CatalogItem) {
      onAdd(item);
      setTerm('');
      setOpen(false);
      setActiveIndex(0);
      setEnterPending(false);
    }

    useEffect(() => {
      if (enterPending && search.fresh) {
        if (results.length > 0) add(results[0]!);
        else setEnterPending(false);
      }
    }, [enterPending, search.fresh]);

    function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
      if (event.key === 'ArrowDown' && results.length > 0) {
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => (current + 1) % results.length);
      } else if (event.key === 'ArrowUp' && results.length > 0) {
        event.preventDefault();
        setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
      } else if (event.key === 'Enter' && term.trim() !== '') {
        event.preventDefault();
        if (search.fresh && results.length > 0)
          add(results[Math.min(activeIndex, results.length - 1)]!);
        else if (!search.fresh) setEnterPending(true);
      } else if (event.key === 'Escape' && open) {
        event.stopPropagation();
        setOpen(false);
      }
    }

    return (
      <div className="relative">
        <label htmlFor={`${id}-input`} className="mb-1 block text-sm font-medium">
          {t('estimates.builder.searchLabel')}
        </label>
        <input
          id={`${id}-input`}
          ref={ref}
          className={FIELD_CLASS}
          value={term}
          placeholder={t('estimates.builder.searchPlaceholder')}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-activedescendant={
            expanded ? optionId(Math.min(activeIndex, results.length - 1)) : undefined
          }
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(event.target.value.trim().length > 0);
            setEnterPending(false);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => setOpen(false)}
        />
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t('estimates.builder.searchResults')}
          hidden={!expanded}
          className="absolute inset-x-0 z-10 mt-1 max-h-72 overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-background)] py-1 text-sm shadow-md"
        >
          {results.map((item, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={item.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              className={cn(
                'flex cursor-pointer items-baseline justify-between gap-3 px-3 py-2',
                index === activeIndex && 'bg-[var(--color-muted)]',
              )}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => add(item)}
            >
              <span className="font-medium">{item.name}</span>
              <span className="shrink-0 tabular-nums text-[var(--color-muted-foreground)]">
                {formatCurrency(Number(item.defaultPrice))}
              </span>
            </li>
          ))}
        </ul>
        {noMatches ? (
          <p role="status" className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {t('estimates.builder.noMatches', { term: term.trim() })}
          </p>
        ) : null}
      </div>
    );
  },
);
